import React, { useEffect, useState, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import api from '../../../api';

const ElectiveStudentsModal = ({
  isOpen,
  onClose,
  classId,
  classObj,
  className,
  notify,
  subjects,
  groupType = 'all',
  electiveSubjects = []
}) => {
  // Normalize class object / id values
  const resolvedClassObj = classObj || (classId && typeof classId === 'object' ? classId : null);
  const classIdValue = resolvedClassObj?._id || resolvedClassObj?.classId || (typeof classId === 'string' ? classId : undefined);
  const tenantIdValue = resolvedClassObj?.tenantId || (classId && typeof classId === 'object' ? classId.tenantId : undefined);
  const academicYearIdValue = resolvedClassObj?.academicYearId || (classId && typeof classId === 'object' ? classId.academicYearId : undefined);
  const semesterIdValue = resolvedClassObj?.semesterId || (classId && typeof classId === 'object' ? classId.semesterId : undefined);
  const isSemesterBased = resolvedClassObj?.isSemesterBased || false;

  const [electiveGroups, setElectiveGroups] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [students, setStudents] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState('');
  const [facultyMap, setFacultyMap] = useState({});
  const [assignedStudentIds, setAssignedStudentIds] = useState([]);
  const [pendingSelectedIds, setPendingSelectedIds] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  // Students are always presented sorted by USN (rollNumber). Direction remains toggleable.
  const [sortConfig, setSortConfig] = useState({ key: 'rollNumber', direction: 'asc' });
  const [expandedGroupId, setExpandedGroupId] = useState(null);

  // Build a comprehensive subject map for all lookups
  const subjectMap = useMemo(() => {
    if (!subjects) return {};
    const allSubjects = Array.isArray(subjects)
      ? subjects
      : Object.values(subjects).flat();
    const map = {};
    allSubjects.forEach(subj => {
      if (subj && subj._id) {
        const id = String(subj._id);
        map[id] = {
          _id: id,
          name: subj.name || subj.subjectName || '',
          code: subj.subjectCode || '',
          isElective: subj.isElective || false,
          isLanguage: subj.isLanguage || false,
          subjectType: subj.subjectType || '',
          credits: subj.credits || 0,
          departmentId: subj.departmentId || '',
          fullName: subj.subjectCode ? `${subj.name || subj.subjectName} [${subj.subjectCode}]` : (subj.name || subj.subjectName)
        };
      }
    });
    return map;
  }, [subjects]);

  // Enhanced subject lookup with better formatting
  const getSubjectDisplay = (subjectId) => {
    if (!subjectId) return { name: 'Unknown Subject', code: '', fullName: 'Unknown Subject' };

    const subject = subjectMap[String(subjectId)];
    if (subject) {
      return subject;
    }

    // Fallback to electiveSubjects array if not in subjectMap
    if (electiveSubjects && electiveSubjects.length > 0) {
      const electiveSubject = electiveSubjects.find(s => String(s._id) === String(subjectId));
      if (electiveSubject) {
        return {
          name: electiveSubject.name || electiveSubject.subjectName || '',
          code: electiveSubject.subjectCode || '',
          fullName: electiveSubject.subjectCode
            ? `${electiveSubject.name || electiveSubject.subjectName} [${electiveSubject.subjectCode}]`
            : (electiveSubject.name || electiveSubject.subjectName)
        };
      }
    }

    return { name: 'Unknown Subject', code: '', fullName: 'Unknown Subject' };
  };

  // Resilient USN accessor — some API responses key this differently
  const getStudentUSN = (student) => {
    if (!student) return '';
    return student.rollNumber || student.USN || student.usn || student.studentUSN || student.regNo || student.registrationNumber || '';
  };

  // Enhanced faculty name display
  const getFacultyDisplay = (facultyId) => {
    if (!facultyId) return { name: 'No Faculty Assigned', shortName: 'No Faculty', initials: 'NF' };

    const key = (typeof facultyId === 'object' && facultyId.$oid)
      ? facultyId.$oid
      : String(facultyId);

    const fullName = facultyMap[key] || facultyMap[facultyId];
    if (!fullName) return { name: 'Loading...', shortName: 'Loading...', initials: '..' };

    const names = fullName.split(' ');
    const initials = names.length >= 2
      ? `${names[0].charAt(0)}${names[names.length - 1].charAt(0)}`
      : names[0].charAt(0);

    return {
      name: fullName,
      shortName: names.length > 1 ? `${names[0]} ${names[names.length - 1].charAt(0)}.` : fullName,
      initials
    };
  };

  // Enhanced filtering for elective groups
  const filteredElectiveGroups = useMemo(() => {
    console.log('🔍 Filtering elective groups:', {
      groupType,
      electiveSubjectsCount: electiveSubjects.length,
      allGroupsCount: electiveGroups.length
    });

    let groups = electiveGroups || [];

    // Filter by electiveSubjects if provided
    if (electiveSubjects && electiveSubjects.length > 0) {
      const electiveSubjectIds = electiveSubjects.map(s => String(s._id));
      groups = groups.filter(group => {
        return group.subjects?.some(subject =>
          electiveSubjectIds.includes(String(subject.subjectId))
        );
      });
    }

    // Filter by groupType
    const filteredByType = groups.filter(group => {
      const groupCategory = String(group.groupCategory || '').toLowerCase();

      if (groupType === 'language') {
        return groupCategory === 'language';
      }

      if (groupType === 'elective') {
        return groupCategory === 'elective';
      }

      return true;
    });

    console.log('✅ Final filtered groups:', filteredByType.length);
    return filteredByType;
  }, [electiveGroups, groupType, electiveSubjects]);

  // Get modal title with badge
  const getModalTitle = () => {
    if (selectedSubject) {
      const groupCategory = selectedSubject.groupObj?.groupCategory;
      const badgeClass = groupCategory === 'language'
        ? 'bg-blue-50 text-blue-700 border-blue-200'
        : 'bg-emerald-50 text-emerald-700 border-emerald-200';
      const badgeText = groupCategory === 'language' ? 'Language' : 'Elective';
      return (
        <div className="flex items-center gap-2.5">
          <h2 className="text-lg font-bold text-slate-900 tracking-tight">
            Manage {groupCategory === 'language' ? 'Language' : 'Elective'} Students
          </h2>
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${badgeClass}`}>
            {badgeText}
          </span>
        </div>
      );
    }

    const title = groupType === 'language' ? 'Language Groups' :
                 groupType === 'elective' ? 'Elective Groups' :
                 'Elective & Language Groups';

    const badgeClass = groupType === 'language' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                      groupType === 'elective' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                      'bg-indigo-50 text-indigo-700 border-indigo-200';

    return (
      <div className="flex items-center gap-2.5">
        <h2 className="text-lg font-bold text-slate-900 tracking-tight">{title}</h2>
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${badgeClass}`}>
          {groupType === 'all' ? 'All Types' : groupType}
        </span>
      </div>
    );
  };

  // Get group color scheme
  const getGroupTheme = (groupCategory) => {
    if (groupCategory === 'language') {
      return {
        bg: 'bg-blue-50/60',
        border: 'border-blue-200',
        text: 'text-blue-800',
        light: 'bg-blue-100',
        badge: 'bg-blue-50 text-blue-700 border border-blue-200',
        icon: 'text-blue-600',
        iconBg: 'bg-blue-100',
        accent: 'bg-blue-600'
      };
    }
    return {
      bg: 'bg-emerald-50/60',
      border: 'border-emerald-200',
      text: 'text-emerald-800',
      light: 'bg-emerald-100',
      badge: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
      icon: 'text-emerald-600',
      iconBg: 'bg-emerald-100',
      accent: 'bg-emerald-600'
    };
  };

  // Statistics
  const stats = useMemo(() => {
    if (!students.length) return null;
    const totalStudents = students.length;
    const assignedCount = assignedStudentIds.length;
    const unassignedCount = totalStudents - assignedCount;
    const assignmentPercentage = totalStudents > 0 ? Math.round((assignedCount / totalStudents) * 100) : 0;
    return {
      totalStudents,
      assignedCount,
      unassignedCount,
      assignmentPercentage
    };
  }, [students, assignedStudentIds]);

  // Filtered and sorted students — always ordered by USN (rollNumber), direction toggleable
  const filteredAndSortedStudents = useMemo(() => {
    let filtered = students;

    if (activeTab === 'assigned') {
      filtered = students.filter(student => assignedStudentIds.includes(student.studentId));
    } else if (activeTab === 'unassigned') {
      filtered = students.filter(student => !assignedStudentIds.includes(student.studentId));
    }

    filtered = filtered.filter(student =>
      (student.name || '').toLowerCase().includes((search || '').toLowerCase()) ||
      getStudentUSN(student).toLowerCase().includes((search || '').toLowerCase()) ||
      ((student.email || '').toLowerCase().includes((search || '').toLowerCase()))
    );

    filtered = [...filtered].sort((a, b) => {
      const aValue = String(getStudentUSN(a));
      const bValue = String(getStudentUSN(b));
      const comparison = aValue.localeCompare(bValue, undefined, { numeric: true, sensitivity: 'base' });
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [students, search, activeTab, assignedStudentIds, sortConfig]);

  // Reset state on open
  useEffect(() => {
    if (isOpen) {
      setSelectedSubject(null);
      setStudents([]);
      setSearch('');
      setError('');
      setAssignedStudentIds([]);
      setPendingSelectedIds([]);
      setActiveTab('all');
      setSortConfig({ key: 'rollNumber', direction: 'asc' });
      setExpandedGroupId(null);
    }
  }, [isOpen, groupType]);

    const fetchFacultyNames = useCallback(async (facultyIds) => {
    const uniqueIds = Array.from(new Set(facultyIds.filter(Boolean)));

    const map = {};
    await Promise.all(uniqueIds.map(async (fid) => {
      let id = fid;
      if (id && typeof id === 'object' && id.$oid) id = id.$oid;
      try {
        const res = await api.get(`/api/admin/user/${tenantIdValue}/${id}`);
        if (res.data && res.data.user) {
          const firstName = res.data.user.firstName || '';
          const lastName = res.data.user.lastName || '';
          map[id] = `${firstName} ${lastName}`.trim() || id;
        }
      } catch (e) {
        console.warn(`Failed to fetch faculty ${id}:`, e);
        map[id] = id;
      }
    }));
    setFacultyMap(map);
  }, [tenantIdValue]);

  const fetchAssignedStudents = useCallback(async () => {
    try {
      if (!selectedSubject) return [];
      const { groupId, subjectId, subGroupId } = selectedSubject;
      const params = {
        groupId,
        subjectId,
        tenantId: tenantIdValue
      };
      if (subGroupId) {
        params.subGroupId = subGroupId;
      }
      const res = await api.get('/api/elective-groups/assigned-students/list', { params });
      return (res.data.data || []).map(id => String(id));
    } catch (err) {
      console.error('Error fetching assigned students:', err);
      notify('Failed to fetch assigned students', 'error');
      return [];
    }
  }, [selectedSubject, tenantIdValue, notify]);

  // Fetch elective groups
  const fetchElectiveGroups = useCallback(async () => {
    try {
      setLoading(true);
      const tenantId = tenantIdValue;
      const academicYearId = academicYearIdValue;
      const semesterId = semesterIdValue;

      if (!tenantId || !academicYearId) {
        console.warn('Missing required IDs for fetching elective groups');
        setElectiveGroups([]);
        return;
      }

      let url = `/api/elective-groups?tenantId=${tenantId}&academicYearId=${academicYearId}`;

      if (isSemesterBased && semesterId) {
        url += `&semesterId=${semesterId}`;
      }

      if (classIdValue) {
        url += `&classId=${classIdValue}`;
      }

      if (groupType && groupType !== 'all') {
        url += `&groupCategory=${groupType}`;
      }

      console.log('Fetching elective groups from:', url);
      const res = await api.get(url);

      const groups = res.data.data || [];
      setElectiveGroups(groups);

      // Collect all facultyIds
      const facultyIds = new Set();
      groups.forEach(group => {
        group.subjects?.forEach(subject => {
          if (subject.facultyId) facultyIds.add(subject.facultyId);
          subject.subGroups?.forEach(sg => {
            if (sg.facultyId) facultyIds.add(sg.facultyId);
          });
        });
      });

      if (facultyIds.size > 0) {
        await fetchFacultyNames(Array.from(facultyIds));
      }
    } catch (err) {
      console.error('Error fetching elective groups:', err);
      setElectiveGroups([]);
      notify('Failed to fetch elective groups', 'error');
    } finally {
      setLoading(false);
    }
  }, [tenantIdValue, academicYearIdValue, classIdValue, semesterIdValue, isSemesterBased, groupType, notify, fetchFacultyNames]);

  useEffect(() => {
    if (!isOpen) return;
    fetchElectiveGroups();
  }, [isOpen, fetchElectiveGroups]);

  // Fetch students
  const fetchStudents = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      if (!classIdValue) {
        setStudents([]);
        setAssignedStudentIds([]);
        setPendingSelectedIds([]);
        return;
      }

      const res = await api.get(`/api/classManager/students-in-class/${classIdValue}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const studentsList = res.data.students || [];
      setStudents(studentsList);

      if (selectedSubject) {
        const assignedIds = await fetchAssignedStudents();
        setAssignedStudentIds(assignedIds);
        setPendingSelectedIds(assignedIds);
      }
    } catch (err) {
      console.error('Error fetching students:', err);
      setError('Failed to fetch students');
      notify('Failed to fetch students', 'error');
    } finally {
      setLoading(false);
    }
  }, [classIdValue, selectedSubject, notify, fetchAssignedStudents]);

  useEffect(() => {
    if (!isOpen) return;
    if (!selectedSubject) {
      setStudents([]);
      setAssignedStudentIds([]);
      setPendingSelectedIds([]);
      return;
    }
    fetchStudents();
  }, [isOpen, selectedSubject, fetchStudents]);



  const handleCheckboxChange = (studentId) => {
    setPendingSelectedIds(prev =>
      prev.includes(studentId)
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  // Bulk operations
  const selectAllVisible = () => {
    const visibleIds = filteredAndSortedStudents.map(student => student.studentId);
    setPendingSelectedIds(prev => Array.from(new Set([...prev, ...visibleIds])));
  };

  const deselectAllVisible = () => {
    const visibleIds = filteredAndSortedStudents.map(student => student.studentId);
    setPendingSelectedIds(prev => prev.filter(id => !visibleIds.includes(id)));
  };

  const assignAll = () => {
    setPendingSelectedIds(students.map(student => student.studentId));
    notify('All students selected for assignment', 'info');
  };

  const unassignAll = async () => {
    if (!selectedSubject) {
      notify('No subject selected', 'warning');
      return;
    }

    setUpdating(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        groupId: selectedSubject.groupId,
        subjectId: selectedSubject.subjectId,
        subGroupId: selectedSubject.subGroupId || null,
        studentIds: [],
        tenantId: tenantIdValue
      };

      await api.post('/api/elective-groups/assign-students', payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setAssignedStudentIds([]);
      setPendingSelectedIds([]);
      notify('All students unassigned successfully', 'success');

      await fetchAssignedStudents();
    } catch (err) {
      console.error('Error unassigning all students:', err);
      notify('Failed to unassign all students', 'error');
    } finally {
      setUpdating(false);
    }
  };

  const handleConfirmMapping = async () => {
    setUpdating(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        groupId: selectedSubject.groupId,
        subjectId: selectedSubject.subjectId,
        subGroupId: selectedSubject.subGroupId || null,
        studentIds: pendingSelectedIds,
        tenantId: tenantIdValue
      };

      await api.post(
        '/api/elective-groups/assign-students',
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      notify('Student assignments updated successfully', 'success');

      const updatedAssignedIds = await fetchAssignedStudents();
      setAssignedStudentIds(updatedAssignedIds);
    } catch (err) {
      console.error('Error confirming mapping:', err);
      notify('Failed to update elective mapping', 'error');
    } finally {
      setUpdating(false);
    }
  };

  const handleRemoveMapping = async (studentId) => {
    setUpdating(true);
    try {
      const token = localStorage.getItem('token');

      const newAssigned = assignedStudentIds.filter(id => id !== studentId);
      const payload = {
        groupId: selectedSubject.groupId,
        subjectId: selectedSubject.subjectId,
        subGroupId: selectedSubject.subGroupId || null,
        studentIds: newAssigned,
        tenantId: tenantIdValue
      };

      await api.post(
        '/api/elective-groups/assign-students',
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      notify('Student removed from elective', 'success');

      const updatedAssignedIds = await fetchAssignedStudents();
      setAssignedStudentIds(updatedAssignedIds);
      setPendingSelectedIds(updatedAssignedIds);
    } catch (err) {
      console.error('Error removing student:', err);
      notify('Failed to remove student from elective', 'error');
    } finally {
      setUpdating(false);
    }
  };

  const handleSort = (key) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const toggleGroupExpand = (groupId) => {
    setExpandedGroupId(prev => prev === groupId ? null : groupId);
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-[100000]">
      <div className="bg-white rounded-3xl shadow-2xl ring-1 ring-black/5 w-full max-w-6xl max-h-[92vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="px-6 sm:px-8 py-5 bg-white border-b border-slate-200 flex-shrink-0">
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0 flex-1">
              {getModalTitle()}
              {selectedSubject && (
                <div className="mt-1.5 flex items-center gap-2 text-sm text-slate-500 flex-wrap">
                  <span className="font-medium text-slate-700 truncate">{selectedSubject.name || getSubjectDisplay(selectedSubject.subjectId).fullName}</span>
                  <span className="text-slate-300">·</span>
                  <span>{className}</span>
                  <span className="text-slate-300">·</span>
                  <span>{students.length} student{students.length !== 1 ? 's' : ''}</span>
                </div>
              )}
            </div>

            <div className="flex items-center gap-5 flex-shrink-0">
              {stats && selectedSubject && (
                <div className="hidden sm:flex items-center gap-5">
                  <div className="text-right">
                    <div className="text-[11px] uppercase tracking-wide font-semibold text-slate-400">Assigned</div>
                    <div className="text-base font-bold text-slate-900">{stats.assignedCount}<span className="text-slate-400 font-medium">/{stats.totalStudents}</span></div>
                  </div>
                  <div className="h-8 w-px bg-slate-200" />
                  <div className="text-right">
                    <div className="text-[11px] uppercase tracking-wide font-semibold text-slate-400">Progress</div>
                    <div className="text-base font-bold text-emerald-600">{stats.assignmentPercentage}%</div>
                  </div>
                </div>
              )}

              <button
                onClick={onClose}
                className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                aria-label="Close"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-auto bg-slate-50">
          {!selectedSubject ? (
            // Group Selection View
            <div className="p-6 sm:p-8">
              <div className="mb-6">
                <h3 className="text-base font-bold text-slate-900">
                  {groupType === 'language' ? 'Select a language group' :
                   groupType === 'elective' ? 'Select an elective group' :
                   'Select a group and subject'}
                </h3>
                <p className="mt-1 text-sm text-slate-500">
                  {groupType === 'language' ?
                    'Choose a language group to manage student assignments.' :
                    groupType === 'elective' ?
                    'Choose an elective group to manage student assignments.' :
                    'Choose an elective or language group to manage student assignments.'
                  }
                </p>
              </div>

              {loading ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <div className="animate-spin rounded-full h-10 w-10 border-[3px] border-slate-200 border-t-indigo-600"></div>
                  <p className="mt-4 text-sm font-medium text-slate-500">Loading groups…</p>
                </div>
              ) : filteredElectiveGroups.length === 0 ? (
                <div className="text-center py-20 bg-white rounded-2xl border border-dashed border-slate-300">
                  <div className="w-14 h-14 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <svg className="w-7 h-7 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                  </div>
                  <h4 className="text-base font-bold text-slate-900 mb-1.5">
                    {groupType === 'language' ? 'No language groups found' :
                     groupType === 'elective' ? 'No elective groups found' :
                     'No groups found'}
                  </h4>
                  <p className="text-sm text-slate-500 max-w-sm mx-auto">
                    {groupType === 'language' ?
                      'Create a language group for this class to get started.' :
                      groupType === 'elective' ?
                      'Create an elective group for this class to get started.' :
                      'No elective or language groups exist for this class yet.'
                    }
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {filteredElectiveGroups.map(group => {
                    const theme = getGroupTheme(group.groupCategory);
                    const isExpanded = expandedGroupId === group._id;

                    return (
                      <div key={group._id} className="bg-white border border-slate-200 rounded-2xl overflow-hidden transition-shadow hover:shadow-md">
                        {/* Group Header */}
                        <div className="p-5">
                          <div className="flex items-start justify-between gap-3 mb-4">
                            <div className="flex items-start gap-3 min-w-0">
                              <div className={`w-10 h-10 rounded-xl ${theme.iconBg} flex items-center justify-center flex-shrink-0`}>
                                <svg className={`w-5 h-5 ${theme.icon}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                </svg>
                              </div>
                              <div className="min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <h4 className="text-base font-bold text-slate-900 truncate">{group.groupName}</h4>
                                  {group.label && (
                                    <span className="inline-flex items-center bg-amber-50 border border-amber-200 text-amber-700 text-[11px] font-semibold px-2 py-0.5 rounded-full">
                                      {group.label}
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className={`inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full ${theme.badge}`}>
                                    {group.groupCategory === 'language' ? 'Language' : 'Elective'}
                                  </span>
                                  <span className="text-xs text-slate-400">
                                    {group.subjects?.length || 0} subject{group.subjects?.length !== 1 ? 's' : ''}
                                  </span>
                                </div>
                              </div>
                            </div>

                            <button
                              onClick={() => toggleGroupExpand(group._id)}
                              className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors flex-shrink-0"
                            >
                              <svg
                                className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                          </div>

                          {/* Group Actions */}
                          <button
                            onClick={() => {
                              if (group.subjects?.length === 1 && !group.subjects[0].subGroups?.length) {
                                const subject = group.subjects[0];
                                const subjectDisplay = getSubjectDisplay(subject.subjectId);
                                setSelectedSubject({
                                  groupId: group._id,
                                  subjectId: subject.subjectId,
                                  subGroupId: null,
                                  subjectObj: subject,
                                  groupObj: group,
                                  subGroupObj: null,
                                  name: subjectDisplay.fullName
                                });
                              } else {
                                toggleGroupExpand(group._id);
                              }
                            }}
                            className="w-full px-4 py-2 bg-slate-900 text-white rounded-xl hover:bg-slate-800 font-semibold text-sm transition-colors"
                          >
                            {group.subjects?.length === 1 && !group.subjects[0].subGroups?.length ? 'Manage students' : 'View subjects'}
                          </button>
                        </div>

                        {/* Expanded Subjects List */}
                        {isExpanded && (
                          <div className="border-t border-slate-200 p-5 bg-slate-50/70 space-y-3">
                            <h5 className="text-[11px] font-bold uppercase tracking-wide text-slate-400">
                              Subjects in this group
                            </h5>

                            <div className="space-y-3">
                              {group.subjects?.map((subject, idx) => {
                                const subjectDisplay = getSubjectDisplay(subject.subjectId);
                                const facultyDisplay = getFacultyDisplay(subject.facultyId);
                                const hasSubGroups = subject.subGroups && subject.subGroups.length > 0;

                                return (
                                  <div key={subject._id || idx} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                                    {/* Subject Header */}
                                    <div className="p-4">
                                      <div className="flex items-start justify-between gap-3 mb-2">
                                        <div className="min-w-0">
                                          <div className="flex items-center gap-2 flex-wrap">
                                            <h6 className="font-semibold text-slate-900 text-sm truncate">{subjectDisplay.fullName}</h6>
                                            {group.label && (
                                              <span className="inline-flex items-center bg-amber-50 border border-amber-200 text-amber-700 text-[10px] font-semibold px-1.5 py-0.5 rounded-full">
                                                {group.label}
                                              </span>
                                            )}
                                          </div>
                                          {subjectDisplay.code && (
                                            <p className="text-xs text-slate-500 mt-0.5">Code: {subjectDisplay.code}</p>
                                          )}
                                          {subject.facultyId && (
                                            <div className="flex items-center gap-2 mt-2">
                                              <div className="w-5 h-5 rounded-full bg-slate-100 flex items-center justify-center">
                                                <span className="text-[10px] font-bold text-slate-600">{facultyDisplay.initials}</span>
                                              </div>
                                              <span className="text-xs text-slate-600">{facultyDisplay.shortName}</span>
                                            </div>
                                          )}
                                        </div>

                                        {!hasSubGroups && (
                                          <button
                                            onClick={() => {
                                              setSelectedSubject({
                                                groupId: group._id,
                                                subjectId: subject.subjectId,
                                                subGroupId: null,
                                                subjectObj: subject,
                                                groupObj: group,
                                                subGroupObj: null,
                                                name: subjectDisplay.fullName
                                              });
                                            }}
                                            className="px-3 py-1.5 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 font-semibold text-xs transition-colors whitespace-nowrap flex-shrink-0"
                                          >
                                            Manage students
                                          </button>
                                        )}
                                      </div>

                                      {/* SubGroups */}
                                      {hasSubGroups && (
                                        <div className="mt-3 border-t border-slate-100 pt-3 space-y-2">
                                          <h6 className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Sections</h6>
                                          {subject.subGroups.map((sg, sgIdx) => {
                                            const subFacultyDisplay = getFacultyDisplay(sg.facultyId);
                                            return (
                                              <div key={sg._id || sgIdx} className="bg-slate-50 border border-slate-200 rounded-lg p-3 flex items-center justify-between gap-3">
                                                <div className="flex items-center gap-3 min-w-0">
                                                  <div className={`w-7 h-7 rounded-lg ${theme.light} flex items-center justify-center flex-shrink-0`}>
                                                    <span className={`text-[11px] font-bold ${theme.text}`}>S{sgIdx + 1}</span>
                                                  </div>
                                                  <div className="min-w-0">
                                                    <div className="flex items-center gap-1.5 flex-wrap">
                                                      <span className="font-medium text-slate-900 text-sm truncate">
                                                        {subjectDisplay.name} · Section {sgIdx + 1}
                                                      </span>
                                                      {group.label && (
                                                        <span className="inline-flex items-center bg-amber-50 border border-amber-200 text-amber-700 text-[10px] font-semibold px-1.5 py-0.5 rounded-full">
                                                          {group.label}
                                                        </span>
                                                      )}
                                                    </div>
                                                    <div className="flex items-center gap-1.5 mt-0.5">
                                                      <div className="w-4 h-4 rounded-full bg-white flex items-center justify-center">
                                                        <span className="text-[9px] font-bold text-slate-600">{subFacultyDisplay.initials}</span>
                                                      </div>
                                                      <span className="text-xs text-slate-500 truncate">{subFacultyDisplay.shortName}</span>
                                                    </div>
                                                  </div>
                                                </div>
                                                <button
                                                  onClick={() => {
                                                    setSelectedSubject({
                                                      groupId: group._id,
                                                      subjectId: subject.subjectId,
                                                      subGroupId: sg.subGroupId,
                                                      subjectObj: subject,
                                                      groupObj: group,
                                                      subGroupObj: sg,
                                                      name: `${subjectDisplay.fullName} - Section ${sgIdx + 1}`
                                                    });
                                                  }}
                                                  className="px-3 py-1.5 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-100 font-semibold text-xs transition-colors whitespace-nowrap flex-shrink-0"
                                                >
                                                  Manage
                                                </button>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            // Student Management View
            <div className="p-6 sm:p-8">
              {/* Subject Info Banner */}
              <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-6">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="w-11 h-11 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center flex-shrink-0">
                      {selectedSubject.groupObj?.groupCategory === 'language' ? (
                        <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      )}
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-base font-bold text-slate-900 truncate">
                        {selectedSubject.name || getSubjectDisplay(selectedSubject.subjectId).fullName}
                      </h3>
                      <div className="flex items-center gap-2 mt-1.5 text-xs text-slate-500 flex-wrap">
                        <span>
                          Faculty: <span className="font-medium text-slate-700">{selectedSubject.subGroupId
                            ? getFacultyDisplay(selectedSubject.subGroupObj?.facultyId).name
                            : getFacultyDisplay(selectedSubject.subjectObj?.facultyId).name}</span>
                        </span>
                        <span className="text-slate-300">·</span>
                        <span>
                          Group: <span className="font-medium text-slate-700">{selectedSubject.groupObj?.groupName}</span>
                        </span>
                        {selectedSubject.subGroupId && (
                          <>
                            <span className="text-slate-300">·</span>
                            <span>
                              Section: <span className="font-medium text-slate-700">{(selectedSubject.subGroupObj && selectedSubject.subjectObj?.subGroups)
                                ? (selectedSubject.subjectObj.subGroups.findIndex(sg => sg.subGroupId === selectedSubject.subGroupObj.subGroupId) + 1)
                                : ''}</span>
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => setSelectedSubject(null)}
                    className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 font-semibold text-sm transition-colors flex-shrink-0"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    Back to groups
                  </button>
                </div>
              </div>

              {/* Control Bar */}
              <div className="bg-white border border-slate-200 rounded-2xl p-4 mb-5">
                <div className="flex flex-col lg:flex-row gap-3">
                  {/* Search */}
                  <div className="flex-1">
                    <div className="relative">
                      <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search by name, USN, or email…"
                        className="w-full pl-10 pr-9 py-2.5 border border-slate-200 bg-slate-50 rounded-xl text-sm outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-300 focus:bg-white transition-colors"
                      />
                      {search && (
                        <button
                          onClick={() => setSearch('')}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Bulk Actions */}
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={selectAllVisible}
                      disabled={updating}
                      className="px-3.5 py-2.5 bg-white border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 font-medium text-xs transition-colors disabled:opacity-50"
                    >
                      Select visible
                    </button>
                    <button
                      onClick={deselectAllVisible}
                      disabled={updating}
                      className="px-3.5 py-2.5 bg-white border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 font-medium text-xs transition-colors disabled:opacity-50"
                    >
                      Deselect visible
                    </button>
                    <button
                      onClick={assignAll}
                      disabled={updating}
                      className="px-3.5 py-2.5 bg-indigo-50 border border-indigo-200 text-indigo-700 rounded-xl hover:bg-indigo-100 font-medium text-xs transition-colors disabled:opacity-50"
                    >
                      Select all
                    </button>
                    <button
                      onClick={unassignAll}
                      disabled={updating}
                      className="px-3.5 py-2.5 bg-rose-50 border border-rose-200 text-rose-700 rounded-xl hover:bg-rose-100 font-medium text-xs transition-colors disabled:opacity-50"
                    >
                      Unassign all
                    </button>
                  </div>
                </div>
              </div>

              {/* Tabs */}
              <div className="inline-flex items-center gap-1 p-1 bg-slate-100 rounded-xl mb-5">
                {['all', 'assigned', 'unassigned'].map(tab => {
                  const count = tab === 'all' ? stats?.totalStudents :
                               tab === 'assigned' ? stats?.assignedCount :
                               stats?.unassignedCount;

                  return (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`px-4 py-1.5 rounded-lg font-semibold text-sm transition-colors ${
                        activeTab === tab
                          ? 'bg-white text-slate-900 shadow-sm'
                          : 'text-slate-500 hover:text-slate-700'
                      }`}
                    >
                      <span className="flex items-center gap-1.5">
                        {tab.charAt(0).toUpperCase() + tab.slice(1)}
                        {count !== undefined && (
                          <span className={`px-1.5 py-0.5 rounded-full text-[11px] font-bold ${
                            activeTab === tab
                              ? 'bg-slate-900 text-white'
                              : 'bg-slate-200 text-slate-600'
                          }`}>
                            {count}
                          </span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Students List */}
              <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                {loading ? (
                  <div className="flex flex-col items-center justify-center py-20">
                    <div className="animate-spin rounded-full h-10 w-10 border-[3px] border-slate-200 border-t-indigo-600"></div>
                    <p className="mt-4 text-sm font-medium text-slate-500">Loading students…</p>
                  </div>
                ) : error ? (
                  <div className="text-center py-20">
                    <div className="w-14 h-14 bg-rose-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <svg className="w-7 h-7 text-rose-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <h4 className="text-base font-bold text-slate-900 mb-1.5">Couldn't load students</h4>
                    <p className="text-sm text-slate-500 mb-5">{error}</p>
                    <button
                      onClick={fetchStudents}
                      className="px-5 py-2 bg-slate-900 text-white rounded-xl hover:bg-slate-800 font-semibold text-sm transition-colors"
                    >
                      Try again
                    </button>
                  </div>
                ) : filteredAndSortedStudents.length === 0 ? (
                  <div className="text-center py-20">
                    <div className="w-14 h-14 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <svg className="w-7 h-7 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5 3.75h.008v.008h-.008v-.008zm-3 0h.008v.008h-.008v-.008z" />
                      </svg>
                    </div>
                    <h4 className="text-base font-bold text-slate-900 mb-1.5">
                      {search ? 'No matching students' : 'No students available'}
                    </h4>
                    <p className="text-sm text-slate-500">
                      {search ? 'Try a different name, USN, or email.' : 'This class has no students yet.'}
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Table Header */}
                    <div className="bg-slate-50 border-b border-slate-200 px-5 py-3 sticky top-0 z-10">
                      <div className="grid grid-cols-12 gap-3 items-center">
                        <div className="col-span-1">
                          <input
                            type="checkbox"
                            checked={filteredAndSortedStudents.every(student =>
                              pendingSelectedIds.includes(student.studentId)
                            )}
                            onChange={(e) =>
                              e.target.checked ? selectAllVisible() : deselectAllVisible()
                            }
                            className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                          />
                        </div>
                        <div className="col-span-4">
                          <span className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Student</span>
                        </div>
                        <div
                          className="col-span-3 flex items-center gap-1 cursor-pointer select-none group"
                          onClick={() => handleSort('rollNumber')}
                        >
                          <span className="text-[11px] font-bold uppercase tracking-wide text-slate-500 group-hover:text-slate-700">USN</span>
                          <span className="text-slate-400 group-hover:text-slate-600 text-xs">
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        </div>
                        <div className="col-span-3">
                          <span className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Email</span>
                        </div>
                        <div className="col-span-1 text-center">
                          <span className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Status</span>
                        </div>
                      </div>
                    </div>

                    {/* Students */}
                    <div className="divide-y divide-slate-100">
                      {filteredAndSortedStudents.map((student) => {
                        const isAssigned = assignedStudentIds.includes(student.studentId);
                        const isPending = pendingSelectedIds.includes(student.studentId);

                        return (
                          <div
                            key={student.studentId}
                            className={`px-5 py-3 transition-colors ${isPending ? 'bg-indigo-50/60' : 'hover:bg-slate-50'}`}
                          >
                            <div className="grid grid-cols-12 gap-3 items-center">
                              <div className="col-span-1">
                                <input
                                  type="checkbox"
                                  checked={isPending}
                                  onChange={() => handleCheckboxChange(student.studentId)}
                                  disabled={updating}
                                  className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                                />
                              </div>

                              <div className="col-span-4 min-w-0">
                                <div className="flex items-center gap-3 min-w-0">
                                  <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center flex-shrink-0">
                                    <span className="text-white text-xs font-bold">
                                      {student.name?.charAt(0).toUpperCase()}
                                    </span>
                                  </div>
                                  <div className="min-w-0">
                                    <div className="font-semibold text-slate-900 text-sm truncate">{student.name}</div>
                                  </div>
                                </div>
                              </div>

                              <div className="col-span-3">
                                <div className="font-mono text-sm text-slate-700">{getStudentUSN(student) || '—'}</div>
                              </div>

                              <div className="col-span-3">
                                <div className="text-sm text-slate-500 truncate">{student.email || '—'}</div>
                              </div>

                              <div className="col-span-1">
                                <div className="flex justify-center">
                                  {isAssigned ? (
                                    <button
                                      onClick={() => handleRemoveMapping(student.studentId)}
                                      disabled={updating}
                                      className="px-2.5 py-1 bg-rose-50 border border-rose-200 text-rose-700 rounded-lg hover:bg-rose-100 font-semibold text-[11px] transition-colors disabled:opacity-50"
                                    >
                                      Remove
                                    </button>
                                  ) : isPending ? (
                                    <span className="px-2.5 py-1 bg-amber-50 border border-amber-200 text-amber-700 rounded-lg font-semibold text-[11px]">
                                      Pending
                                    </span>
                                  ) : (
                                    <span className="px-2.5 py-1 bg-slate-50 border border-slate-200 text-slate-500 rounded-lg font-semibold text-[11px]">
                                      Unassigned
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Action Bar */}
        {selectedSubject && (
          <div className="border-t border-slate-200 px-6 sm:px-8 py-4 bg-white flex-shrink-0">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-0">
                {pendingSelectedIds.length > 0 && (
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="px-3 py-1.5 bg-indigo-50 rounded-lg">
                      <span className="text-sm font-semibold text-indigo-700">
                        {pendingSelectedIds.length} selected
                      </span>
                    </div>
                    {(() => {
                      const added = pendingSelectedIds.filter(id => !assignedStudentIds.includes(id)).length;
                      const removed = assignedStudentIds.filter(id => !pendingSelectedIds.includes(id)).length;
                      if (added > 0 || removed > 0) {
                        return (
                          <div className="flex items-center gap-3 text-xs text-slate-500">
                            {added > 0 && (
                              <span className="flex items-center gap-1 font-medium text-emerald-600">
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                </svg>
                                {added} to add
                              </span>
                            )}
                            {removed > 0 && (
                              <span className="flex items-center gap-1 font-medium text-rose-600">
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                                {removed} to remove
                              </span>
                            )}
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-3 flex-shrink-0">
                <button
                  onClick={onClose}
                  className="px-5 py-2.5 bg-white border border-slate-300 text-slate-700 rounded-xl hover:bg-slate-50 font-semibold text-sm transition-colors"
                >
                  Cancel
                </button>

                {(() => {
                  const added = pendingSelectedIds.filter(id => !assignedStudentIds.includes(id)).length;
                  const removed = assignedStudentIds.filter(id => !pendingSelectedIds.includes(id)).length;
                  const hasChanges = added > 0 || removed > 0;

                  let buttonText = 'Confirm changes';
                  if (!updating) {
                    if (added > 0 && removed === 0) buttonText = `Assign ${added} student${added !== 1 ? 's' : ''}`;
                    else if (removed > 0 && added === 0) buttonText = `Unassign ${removed} student${removed !== 1 ? 's' : ''}`;
                    else if (hasChanges) buttonText = `Apply changes`;
                  }

                  return (
                    <button
                      onClick={handleConfirmMapping}
                      disabled={updating || !hasChanges}
                      className="px-5 py-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 font-semibold text-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {updating ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/40 border-t-white"></div>
                          Saving…
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                          </svg>
                          {buttonText}
                        </>
                      )}
                    </button>
                  );
                })()}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default ElectiveStudentsModal;